import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import rtlPlugin from "@mui/stylis-plugin-rtl";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Provider, useDispatch, useSelector } from "react-redux";
import { prefixer } from "stylis";
import DatePickersProvider from "./components/DatePickersProvider.jsx";
import Layout from "./components/Layout.jsx";
import { settings } from "./config/settings";
import BranchesPage from "./pages/BranchesPage.jsx";
import ContactReasonsPage from "./pages/ContactReasonsPage.jsx";
import CommunicationMethodsPage from "./pages/CommunicationMethodsPage.jsx";
import ShortSubjectsPage from "./pages/ShortSubjectsPage.jsx";
import CreateTicketPage from "./pages/CreateTicketPage.jsx";
import CustomersPage from "./pages/CustomersPage.jsx";
import DepartmentsPage from "./pages/DepartmentsPage.jsx";
import EditTicketPage from "./pages/EditTicketPage.jsx";
import HomePage from "./pages/HomePage.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import ProductsPage from "./pages/ProductsPage.jsx";
import ReportsPage from "./pages/ReportsPage.jsx";
import TicketViewPage from "./pages/TicketViewPage.jsx";
import TicketsListPage from "./pages/TicketsListPage.jsx";
import ProfilePage from "./pages/ProfilePage.jsx";
import SettingsPage from "./pages/SettingsPage.jsx";
import UsersPage from "./pages/UsersPage.jsx";
import ActivityLogPage from "./pages/ActivityLogPage.jsx";
import ActionTypesPage from "./pages/ActionTypesPage.jsx";
import EmployeesPage from "./pages/EmployeesPage.jsx";
import ActionsReportPage from "./pages/ActionsReportPage.jsx";
import { store } from "./store";
import { clearUser, updateAuthProfile } from "./store/slices/authSlice";
import { applyUserSiteStyle } from "./store/slices/themeSlice";
import { createAppTheme } from "./theme/themeConfig";
import { authAPI } from "./services/api";
import { canAccessRoute } from "./utils/permissions";
import {
  clearPendingTicketRedirect,
  consumePendingTicketRedirect,
  parseTicketIdFromLocation,
  savePendingTicketRedirect,
} from "./utils/ticketDeepLink";

// Create RTL cache
const rtlCache = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create LTR cache
const ltrCache = createCache({
  key: "muiltr",
  stylisPlugins: [prefixer],
});

// Simple routing without React Router to avoid Kaspersky issues
const AppContent = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const [currentPage, setCurrentPage] = React.useState("login");
  const [currentTicketId, setCurrentTicketId] = React.useState(null);
  const [ticketViewRefreshKey, setTicketViewRefreshKey] = React.useState(0);
  const [isRTL, setIsRTL] = React.useState(i18n.language === "ar");
  const siteStyle = useSelector((state) => state.theme.siteStyle);
  const theme = React.useMemo(
    () => createAppTheme(isRTL ? "rtl" : "ltr", siteStyle),
    [isRTL, siteStyle]
  );

  const openTicketView = useCallback((ticketId) => {
    setCurrentTicketId(String(ticketId));
    setCurrentPage("ticket-view");
  }, []);

  const resolveAppRoute = useCallback(
    (isAuthenticated, ticketIdFromUrl = null) => {
      const ticketId = ticketIdFromUrl || parseTicketIdFromLocation();

      if (ticketId) {
        if (isAuthenticated) {
          openTicketView(ticketId);
        } else {
          savePendingTicketRedirect(ticketId);
          setCurrentTicketId(null);
          setCurrentPage("login");
        }
        return;
      }

      if (isAuthenticated) {
        setCurrentTicketId(null);
        setCurrentPage("home");
      } else {
        setCurrentTicketId(null);
        setCurrentPage("login");
      }
    },
    [openTicketView]
  );

  useEffect(() => {
    const validateStoredAuth = async () => {
      const ticketIdFromUrl = parseTicketIdFromLocation();
      const token = localStorage.getItem(settings.STORAGE_KEYS.AUTH_TOKEN);
      const userData = localStorage.getItem(settings.STORAGE_KEYS.USER_DATA);

      try {
        await authAPI.ensureCsrf();
      } catch {
        // CSRF cookie is best-effort; unsafe requests will retry.
      }

      if (!token || !userData) {
        if (token || userData) {
          dispatch(clearUser());
        }
        resolveAppRoute(false, ticketIdFromUrl);
        return;
      }

      try {
        const profileData = await authAPI.checkAuth();
        dispatch(updateAuthProfile(profileData));
        resolveAppRoute(true, ticketIdFromUrl);
      } catch {
        dispatch(clearUser());
        resolveAppRoute(false, ticketIdFromUrl);
      }
    };

    validateStoredAuth();
  }, [dispatch, resolveAppRoute]);

  // Set document direction based on language
  useEffect(() => {
    const rtl = i18n.language === "ar";
    setIsRTL(rtl);
    document.documentElement.dir = rtl ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const { user, permissions, isAuthenticated } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentPage("login");
      setCurrentTicketId(null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (user?.site_style) {
      dispatch(applyUserSiteStyle(user.site_style));
    }
  }, [dispatch, user?.site_style]);

  const handleLoginSuccess = () => {
    const pendingTicketId = consumePendingTicketRedirect();
    if (pendingTicketId) {
      openTicketView(pendingTicketId);
      return;
    }
    setCurrentPage("home");
  };

  const handleLogout = () => {
    clearPendingTicketRedirect();
    setCurrentTicketId(null);
    setCurrentPage("login");
  };

  const handleNavigate = (path) => {
    if (!canAccessRoute(permissions, path)) {
      setCurrentTicketId(null);
      setCurrentPage("home");
      return;
    }

    const ticketEditMatch = path.match(/^\/tickets\/(\d+)\/edit$/);
    if (ticketEditMatch) {
      setCurrentTicketId(ticketEditMatch[1]);
      setCurrentPage("ticket-edit");
      return;
    }

    // Extract ticket ID from path if it's a ticket view path
    const ticketMatch = path.match(/^\/tickets\/(\d+)$/);
    if (ticketMatch) {
      setCurrentTicketId(ticketMatch[1]);
      setTicketViewRefreshKey((prev) => prev + 1);
      setCurrentPage("ticket-view");
      return;
    }

    // Clear ticket ID for other pages
    setCurrentTicketId(null);

    switch (path) {
      case "/dashboard":
      case "/home":
        setCurrentPage("home");
        break;
      case "/tickets":
      case "/search":
        setCurrentPage("tickets");
        break;
      case "/create-ticket":
        setCurrentPage("create-ticket");
        break;
      case "/customers":
        setCurrentPage("customers");
        break;
      case "/contact-reasons":
        setCurrentPage("contact-reasons");
        break;
      case "/communication-methods":
        setCurrentPage("communication-methods");
        break;
      case "/short-subjects":
        setCurrentPage("short-subjects");
        break;
      case "/branches":
        setCurrentPage("branches");
        break;
      case "/products":
        setCurrentPage("products");
        break;
      case "/departments":
        setCurrentPage("departments");
        break;
      case "/users":
        setCurrentPage("users");
        break;
      case "/reports":
        setCurrentPage("reports");
        break;
      case "/action-types":
        setCurrentPage("action-types");
        break;
      case "/employees":
        setCurrentPage("employees");
        break;
      case "/actions-report":
        setCurrentPage("actions-report");
        break;
      case "/activity-log":
        setCurrentPage("activity-log");
        break;
      case "/profile":
        setCurrentPage("profile");
        break;
      case "/settings":
        setCurrentPage("settings");
        break;
      default:
        setCurrentPage("home");
    }
  };

  const renderPageContent = () => {
    if (!isAuthenticated) {
      return <LoginPage onLoginSuccess={handleLoginSuccess} />;
    }

    switch (currentPage) {
      case "home":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <HomePage onNavigate={handleNavigate} />
          </Layout>
        );
      case "tickets":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <TicketsListPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "create-ticket":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <CreateTicketPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "customers":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <CustomersPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "contact-reasons":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <ContactReasonsPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "communication-methods":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <CommunicationMethodsPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "short-subjects":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <ShortSubjectsPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "branches":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <BranchesPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "products":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <ProductsPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "departments":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <DepartmentsPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "users":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <UsersPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "reports":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <ReportsPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "action-types":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <ActionTypesPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "employees":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <EmployeesPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "actions-report":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <ActionsReportPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "activity-log":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <ActivityLogPage onNavigate={handleNavigate} />
          </Layout>
        );
      case "profile":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <ProfilePage onNavigate={handleNavigate} />
          </Layout>
        );
      case "settings":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <SettingsPage />
          </Layout>
        );
      case "ticket-edit":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <EditTicketPage
              ticketId={currentTicketId}
              onNavigate={handleNavigate}
            />
          </Layout>
        );
      case "ticket-view":
        return (
          <Layout onLogout={handleLogout} onNavigate={handleNavigate}>
            <TicketViewPage
              key={ticketViewRefreshKey}
              ticketId={currentTicketId}
              onNavigate={handleNavigate}
            />
          </Layout>
        );
      default:
        return <LoginPage onLoginSuccess={handleLoginSuccess} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <DatePickersProvider>
        <CssBaseline />
        <CacheProvider value={isRTL ? rtlCache : ltrCache}>
          <div dir={isRTL ? "rtl" : "ltr"}>{renderPageContent()}</div>
        </CacheProvider>
      </DatePickersProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
